// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds.

type Now<T> = () => T | undefined;

export const DEBOUNCE_REUSE_WINDOW = 50; // ms - if a new call comes within this window before the scheduled execution, we can reuse the existing timeout

export type DebouncedFunction<A extends any[], T> = {
  readonly now: Now<T>;
  readonly cancel: () => void;
  readonly isPending: () => boolean;
  (..._args: A): {
    readonly now: Now<T>;
  };
};

function debounceInternal<A extends any[], T>(func: (...args: A) => T, wait: number): DebouncedFunction<A, T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let scheduledTime = 0;
  let args: unknown[];
  let context: unknown;

  const execute = (): T | undefined => {
    timeoutId = null;
    scheduledTime = 0;
    return func.apply(context, args);
  };

  const clearTimer = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
      scheduledTime = 0;
    }
  };

  const scheduleExecution = () => {
    const now = Date.now();
    const targetTime = now + wait;

    const reuseWindow = Math.min(wait / 4, DEBOUNCE_REUSE_WINDOW);

    // If we already have a timeout scheduled close enough, just update args and reuse it
    if (timeoutId !== null && scheduledTime > now && (scheduledTime + reuseWindow) >= targetTime) {
      // Reuse existing timeout - just update the args/context; MD-6478
      return;
    }

    // Clear old timeout and create new one only when necessary
    clearTimer();
    scheduledTime = targetTime;
    timeoutId = setTimeout(execute, wait);
  };

  // Has to be 'function' because of execution context.
  const res = function (this: any, ...latestArgs: unknown[]): { readonly now: Now<T> } {
    context = this;
    args = latestArgs;
    scheduleExecution();

    return {
      now: (): T | undefined => {
        if (timeoutId !== null) {
          clearTimer();
          return func.apply(context, args);
        }
        return undefined;
      },
    };
  };

  return Object.assign(res, {
    now: (): T | undefined => {
      if (timeoutId !== null) {
        clearTimer();
        return execute();
      }
      return undefined;
    },
    cancel: (): void => {
      clearTimer();
    },
    isPending: (): boolean => {
      return timeoutId !== null;
    },
  });
}

export function debounce<A extends any[], T>(func: (...args: A) => T, wait: number): DebouncedFunction<A, T> {
  return debounceInternal<A, T>(func, wait);
}
